import React from'react';
import {
 BarChart as RechartsBarChart,
 Bar,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 ResponsiveContainer,
 Legend,
 Cell,
 LabelList,
} from'recharts';
import ExportButton from'../ExportButton';

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316'];

export default function BarChartComponent({
 data,
 dataKeys,
 xAxisKey ='name',
 title,
 height = 300,
 stacked = false,
 horizontal = false,
 colorByIndex = false,  showValueLabels = false,
  exportFilename,
  onBarClick,
  valueFormat = 'number',
  descriptionKey,
}) {
 const ChartComponent = RechartsBarChart;
 const layout = horizontal ?'vertical' :'horizontal';

 // Formats numeric bar values for labels + tooltip. Supports plain numbers
 // (default) and currency so the same component can render both a call-count
 // chart and an estimated-cost chart legibly.
 const formatValue = (v) => {
 const n = Number(v) || 0;
 if (valueFormat === 'currency') {
 return `$${n.toLocaleString(undefined, {
 minimumFractionDigits: n !== 0 && Math.abs(n) < 1 ? 2 : 2,
 maximumFractionDigits: 2,
 })}`;
 }
 return n.toLocaleString();
 };  const handleClick = (entry, index) => {
    if (onBarClick && data[index]) {
      onBarClick(data[index], index);
    }
  };

  // Rich tooltip: always shows the FULL category label (which may be
  // truncated on the axis) plus the value, and — when a `descriptionKey` is
  // provided — a plain-English explanation. Generic, so any chart that passes
  // `descriptionKey` gets the same behaviour for free.
  const RichTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload || {};
    const fullLabel = row?.[xAxisKey];
    const desc = descriptionKey ? row?.[descriptionKey] : null;
    return (
      <div
        style={{
          backgroundColor: 'var(--tooltip-bg, #fff)',
          color: 'var(--tooltip-fg, #111827)',
          border: '1px solid var(--tooltip-border, #e5e7eb)',
          borderRadius: '8px',
          fontSize: '12px',
          padding: '8px 10px',
          maxWidth: 280,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        {fullLabel != null && (
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{String(fullLabel)}</div>
        )}
        {payload.map((p) => (
          <div key={p.dataKey} style={{ color: p.color }}>
            {p.name}: <strong>{formatValue(p.value)}</strong>
          </div>
        ))}
        {desc && (
          <div style={{ marginTop: 6, color: 'var(--tooltip-muted, #6b7280)', lineHeight: 1.45 }}>
            {String(desc).split('\n').map((line, i) => (
              <div key={i} style={{ marginTop: i === 0 ? 0 : 2 }}>{line}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

 // When rendering a horizontal bar chart with value labels, the label is
 // drawn just past the END of the longest bar. Without extra right padding
 // that label gets clipped by the plot edge (e.g. "$1.7…" cut off). Reserve
 // room proportional to the longest formatted label so it always fits.
 const longestLabelChars = React.useMemo(() => {
 if (!showValueLabels || !Array.isArray(data) || data.length === 0) return 0;
 let max = 0;
 for (const row of data) {
 for (const key of dataKeys) {
 const len = String(formatValue(row?.[key])).length;
 if (len > max) max = len;
 }
 }
 return max;
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [data, dataKeys, showValueLabels, valueFormat]);  // ~7px per character at fontSize 11, plus a little breathing room.
  const rightMargin = horizontal && showValueLabels
    ? Math.min(120, Math.max(24, longestLabelChars * 7 + 12))
    : 5;

  // Vertical (default) bar charts with many categories crowd their X-axis
  // labels on narrow screens. Once we pass a threshold, angle the labels and
  // reserve bottom room so nothing overlaps or gets clipped — key for the
  // latency histogram, which can have 15–30+ buckets.
  const categoryCount = Array.isArray(data) ? data.length : 0;
  const angleXLabels = !horizontal && categoryCount > 6;
  const longestCatChars = React.useMemo(() => {
    if (horizontal || !Array.isArray(data)) return 0;
    let max = 0;
    for (const row of data) {
      const len = String(row?.[xAxisKey] ?? '').length;
      if (len > max) max = len;
    }
    return max;
  }, [data, xAxisKey, horizontal]);
  const bottomMargin = angleXLabels
    ? Math.min(70, Math.max(24, longestCatChars * 5))
    : 5;

 return (
 <div className="chart-container">
 <div className="flex items-center justify-between mb-4">
 {title && <h3 className="text-sm font-semibold text-gray-700">{title}</h3>}
 {exportFilename && <ExportButton data={data} filename={exportFilename} />}
 </div>      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={data} layout={layout} margin={{ top: 5, right: rightMargin, bottom: bottomMargin, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
          {horizontal ? (
            <>
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatValue(v)} />
              <YAxis
                dataKey={xAxisKey}
                type="category"
                tick={{ fontSize: 11 }}
                width={170}
                interval={0}
                tickFormatter={(v) => {
                  const s = String(v ?? '');
                  return s.length > 28 ? `${s.slice(0, 27)}…` : s;
                }}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={angleXLabels ? 4 : 8}
                angle={angleXLabels ? -35 : 0}
                textAnchor={angleXLabels ? 'end' : 'middle'}
                height={angleXLabels ? bottomMargin + 15 : 30}
              />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatValue(v)} allowDecimals={false} width={44} />
            </>
          )}
 <Tooltip
 content={<RichTooltip />}
 contentStyle={{
 backgroundColor:'var(--tooltip-bg, #fff)',
 color:'var(--tooltip-fg, #111827)',
 border:'1px solid var(--tooltip-border, #e5e7eb)',
 borderRadius:'8px',
 fontSize:'12px',
 }}
 labelStyle={{ color:'var(--tooltip-fg, #111827)' }}
 itemStyle={{ color:'var(--tooltip-fg, #111827)' }}
 formatter={(value) => formatValue(value)}
 />
 <Legend iconSize={10} wrapperStyle={{ fontSize:'12px' }} />
 {dataKeys.map((key, idx) => (
 <Bar
 key={key}
 dataKey={key}
 fill={COLORS[idx % COLORS.length]}
 stackId={stacked ?'stack' : undefined}
 radius={stacked ? undefined : [4, 4, 0, 0]}
 maxBarSize={40}
 onClick={onBarClick ? handleClick : undefined}
 cursor={onBarClick ?'pointer' : undefined}
 >
 {colorByIndex &&
 data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
 {showValueLabels && (
 <LabelList
 dataKey={key}
 position={horizontal ?'right' :'top'}
 style={{ fontSize: 11, fill:'var(--tooltip-fg, #374151)' }}
 formatter={(v) => formatValue(v)}
 />
 )}
 </Bar>
 ))}
 </ChartComponent>
 </ResponsiveContainer>
 </div>
 );
}
